import React from 'react'
import { VrModulePanel, VrModuleTitle } from '../layout'
import { css } from '@emotion/css'
import { RouteCoveragePieChart } from '../../../routes-coverage/pie-chart'
import { theme as deprecatedTheme } from '../../../../styled'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { LoadingMaskDonut } from '../../../../legacy-design-system/loading-masks'
import { DecimalShorthand } from '../../../../legacy-design-system/count-shorthand'
import { SelectedValueReportingFacet } from '../../../../graphql/schema/types/value-reporting'
import { ErrorBoundary } from '../../../../legacy-design-system/error-boundary/error-boundary'
import { VrModuleRouteCoverageData } from './index'
import { FormattedMessage } from 'react-intl'
import { responsiveLayoutWidth } from '../layout/vr-module-containers'
import { useVrRouteCoverage } from './api/use-vr-route-coverage'

const cssSummaryText = css`
  color: ${theme.color.neutral60};
  font-family: ${deprecatedTheme.fontFamilies.secondary};
  max-width: 160px;
`

const cssPanel = css`
  @media all and (min-width: ${responsiveLayoutWidth}) {
    /** Route coverage should not take up as much width space as other row modules. */
    max-width: 450px;
  }
`

const chartSize = 200

export interface VrModuleOverallRouteCoverageProps {
  parentIsLoading: boolean
  readonly organizationUuid: string
  readonly selectedFacets: SelectedValueReportingFacet[]
}

export function VrModuleRouteCoverage({
  parentIsLoading,
  organizationUuid,
  selectedFacets
}: VrModuleOverallRouteCoverageProps) {
  return (
    <VrModulePanel
      hasBorder={false}
      order={3}
      minHeight={315}
      testId="value-reporting-route-coverage"
      className={cssPanel}
    >
      <VrModuleTitle id="title_route_coverage" />
      <ErrorBoundary>
        <VrRouteCoverageLoader
          parentIsLoading={parentIsLoading}
          organizationUuid={organizationUuid}
          selectedFacets={selectedFacets}
        />
      </ErrorBoundary>
    </VrModulePanel>
  )
}

function VrRouteCoverageLoader({
  parentIsLoading,
  organizationUuid,
  selectedFacets
}: VrModuleOverallRouteCoverageProps) {
  const { data, isLoading, error } = useVrRouteCoverage(
    organizationUuid,
    selectedFacets
  )

  if (error) {
    throw new Error(error.message)
  }

  if (parentIsLoading || isLoading || !data) {
    return <LoadingView />
  } else {
    if (!data.facets) {
      throw new Error('Unexpected error: facets must be defined')
    }
    return <DataView data={data.facets} />
  }
}

function LoadingView() {
  const testId = 'value-reporting-route-coverage-loading'
  return (
    <Wrapper dataTestId={testId} e2eTestId={testId}>
      <span
        className={css`
          @media all and (min-width: ${responsiveLayoutWidth}) {
            margin-left: 110px;
          }
          @media all and (max-width: ${responsiveLayoutWidth}) {
            /** Loading mask should be centered when on small screens */
            justify-content: center;
            display: flex;
            width: 100%;
          }
        `}
      >
        <LoadingMaskDonut
          frameSize={chartSize}
          id="vr-route-coverage"
          innerCircleRadius={65}
          radius={80}
          margin={0}
        />
      </span>
    </Wrapper>
  )
}

function DataView({ data }: { readonly data: VrModuleRouteCoverageData }) {
  const wrapperTestId = 'value-reporting-route-coverage-loaded'
  return (
    <Wrapper dataTestId={wrapperTestId} e2eTestId={wrapperTestId}>
      <div
        className={css`
          display: flex;
          @media all and (max-width: ${responsiveLayoutWidth}) {
            /** Content view should be centered on small screens */
            justify-content: center;
          }
        `}
      >
        <div
          className={cssSummaryText}
          data-e2e="value-reporting-route-coverage-summary"
          data-testid="value-reporting-route-coverage-summary"
        >
          <FormattedMessage
            id="detail_routesExercisedOutOfDiscovered"
            values={{
              exercised: <DecimalShorthand count={data.routesExercised} />,
              discovered: (
                <b>
                  <DecimalShorthand
                    count={data.routesDiscovered}
                    returnPlaneString={true}
                  />
                </b>
              )
            }}
          />
        </div>
        <RouteCoveragePieChart size={chartSize} {...data} />
      </div>
    </Wrapper>
  )
}

const Wrapper = ({
  children,
  dataTestId
}: {
  readonly children: React.ReactNode
  dataTestId?: string
  readonly e2eTestId?: string
}) => (
  <div
    data-testid={dataTestId}
    className={css`
      display: flex;
      flex-direction: column;
      width: 100%;
    `}
  >
    {children}
  </div>
)
